import KiccEasyCardApi from '@apis/easyCard/KiccEasyCardApi';
import kiccEasyCardQueries from '@apis/queries/kiccEasyCardQueries';
import useDeviceId from '@hooks/native/useDeviceId';
import useRegisterPin from '@hooks/native/useRegisterPin';
import useLoading from '@hooks/ui/useLoading';
import { ErrorResponse } from '@models/error';
import { useFlow, useStepFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

/** 본인인증으로 받은 토큰으로 PIN을 재설정한다. 생체 인증을 체크했으면 이 기기도 함께 등록한다 */
function useResetPin(pinResetToken?: string) {
    const { t } = useTranslation('easyCard');
    const { startLoading, stopLoading } = useLoading();
    const { pop } = useFlow();
    const { replaceStep } = useStepFlow('EasyCardActivity');
    const deviceId = useDeviceId();
    const queryClient = useQueryClient();
    const { registerPin } = useRegisterPin({
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: kiccEasyCardQueries.bioDevice._def });
            toast.success(t('pin.reset.success'));
            pop();
            replaceStep({ isInitialPinSetting: false });
        },
        onError() {
            toast.error('생체인증에 실패했어요.');
        },
    });
    // 실패해서 다시 입력받을 때마다 값이 바뀐다
    const [attempt, setAttempt] = useState(0);
    const { mutate } = useMutation({
        // 생체 인증 등록까지 마쳤는지를 돌려준다. PIN은 이미 바뀌었으므로 등록 실패는 오류로 올리지 않는다
        async mutationFn({ pin, useBio, token }: { pin: string; useBio: boolean; token: string }) {
            await KiccEasyCardApi.resetPin({ pin, checkPin: pin, pinResetToken: token });
            if (!useBio) {
                return true;
            }
            if (!deviceId) {
                return false;
            }
            try {
                await KiccEasyCardApi.setBioDevice({ pin, deviceId });
                return true;
            } catch (e) {
                console.error(e);
                return false;
            }
        },
        onMutate() {
            startLoading();
        },
        onSuccess(isBioDeviceSet, request) {
            if (!isBioDeviceSet) {
                toast.error(t('pin.bioDevice.error'));
                pop();
                replaceStep({ isInitialPinSetting: false });
                return;
            }
            registerPin(request.pin);
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            // 본인인증 토큰이 유효하지 않으면 다시 입력해도 재설정할 수 없으므로 돌려보낸다
            if (e.response?.data?.code === 'IDENTITY_VERIFICATION_FAILED') {
                toast.error(t('pin.reset.invalidAccess'));
                pop();
                return;
            }
            toast.error(t('pin.reset.error'));
            setAttempt((prev) => prev + 1);
        },
        onSettled() {
            stopLoading();
        },
    });

    const handleResetPin = (pin: string, useBio: boolean) => {
        if (!pinResetToken) {
            return;
        }
        mutate({ pin, useBio, token: pinResetToken });
    };

    return {
        attempt,
        handleResetPin,
    };
}

export default useResetPin;
